'use client';

import { Fragment } from 'react';
import { useRouter } from 'next/navigation';
import { Button, Card, Separator, Skeleton, toast } from '@mokoboko/components';

import { useGoBack } from '@/hooks/use-go-back';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { ChevronDown } from '@/icons';
import { PageContainer } from './page-container';
import { GiftCard } from './gift-card';
import { FaqSection } from './faq-section';
import { PickYourClassSection } from './pick-your-class-section';
import { GiftCardPlansSection } from './gift-card-plans-section';
import { TermsLinkRow } from './terms-link-row';
import { CartData } from '@/lib/types';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { DESCRIPTION_PROSE } from '@/lib/prose';
import { GIFT_CARD_ASPECT } from '@/lib/gift-card-design';
import { goToCheckout } from '@/helpers/checkout-navigation';
import type { EmbedHideKey } from '@/helpers/embed-hides';
import {
  giftCardCreditLine,
  giftCardMeta,
  giftCardRedeemWithin,
  giftCardValue,
} from '@/helpers/gift-card';
import useFaqs from '@/state/faqs';
import { useRoleTerms } from '@/state/public-terms';
import useTenant from '@/state/tenant';
import type { GiftCard as GiftCardProduct } from '@/state/gift-cards';

const NO_HIDES: ReadonlySet<EmbedHideKey> = new Set();

// No eligibility check runs here: the buyer is not the one who uses the card.
// A gift card has no appearance settings, so an embed's hides are read as they
// are rather than folded into show flags.
export const GiftCardDetails = ({
  giftCard,
  hides = NO_HIDES,
}: {
  giftCard: GiftCardProduct;
  hides?: ReadonlySet<EmbedHideKey>;
}) => {
  const router = useRouter();
  const { goBack, hasBack } = useGoBack();
  const isMobile = useIsMobile();
  const { data: tenant } = useTenant();
  const { data: faqs, isLoading: isFaqsLoading } = useFaqs({
    entityType: 'GIFT_CARD',
    slug: giftCard.slug,
  });
  const { docs: termsDocs } = useRoleTerms({
    type: 'GIFT_CARD',
    slug: giftCard.slug,
  });
  const creditLine = giftCardCreditLine(giftCard);

  const handleCheckout = () => {
    const cartData: CartData<'gift-card'> = {
      items: [{ ...giftCard, quantity: 1 }],
      totalAmount: giftCard.price,
      checkoutType: 'gift-card',
    };

    if (!goToCheckout(router, cartData)) {
      toast.info('Allow pop-ups to continue to checkout.');
      return;
    }

    trackMetaConversion({
      event: 'AddToCart',
      custom_data: {
        content_name: giftCard.name,
        content_type: 'gift_card',
        content_ids: [giftCard.slug],
        value: giftCard.price,
        currency: giftCard.currency,
      },
    });
    track({
      event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
      properties: {
        product_type: 'gift_card',
        product_name: giftCard.name,
        product_id: giftCard.gift_card_id,
        price: giftCard.price,
        currency: giftCard.currency,
        quantity: 1,
      },
    });
  };

  const desktopBuyButton = (
    <span className="hidden w-fit shrink-0 sm:block">
      <Button onClick={handleCheckout} size={isMobile ? 'lg' : 'md'}>
        Buy gift card
      </Button>
    </span>
  );

  return (
    <PageContainer>
      <Card contentClassName="p-5 sm:p-6 flex flex-col gap-6">
        {hasBack && (
          <>
            <div className="flex items-center justify-between">
              <Button
                variant={'secondary-gray'}
                className="w-fit"
                leadingIcon={<ChevronDown rotate={90} />}
                onClick={() => goBack('/gift-cards')}
                size={isMobile ? 'lg' : 'md'}
              >
                Go back
              </Button>
              {desktopBuyButton}
            </div>
            <Separator />
          </>
        )}

        <div className="flex flex-col gap-5 sm:flex-row sm:gap-16">
          <div className="order-2 flex flex-1 flex-col gap-5 sm:order-1 sm:gap-6">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="flex flex-1 flex-col gap-1">
                <h1 className="heading-page">{giftCard.name}</h1>

                <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-600">
                  {giftCardMeta(giftCard).map((part, index) => (
                    <Fragment key={part}>
                      {index > 0 && <span className="text-gray-400">·</span>}
                      <span>{part}</span>
                    </Fragment>
                  ))}
                </div>

                {creditLine && (
                  <p className="text-sm text-gray-600">{creditLine}</p>
                )}
                <p className="text-sm text-gray-600">
                  {giftCardRedeemWithin(giftCard)}
                </p>
              </div>
              {!hasBack && desktopBuyButton}
            </div>

            {hides.has('description') ? null : giftCard.description ? (
              <div
                className={DESCRIPTION_PROSE}
                dangerouslySetInnerHTML={{ __html: giftCard.description }}
              />
            ) : (
              <p className={DESCRIPTION_PROSE}>{giftCard.short_description}</p>
            )}
          </div>

          {/* The card is where the pack page's cover image sits; the heading
              and the meta line already say everything it shows. */}
          {!hides.has('image') && (
            <div aria-hidden className="order-1 shrink-0 sm:order-2">
              <GiftCard
                studioName={tenant?.name ?? ''}
                look={giftCard.look}
                headline={giftCardValue(giftCard)}
                className="sm:w-[280px]"
              />
            </div>
          )}
        </div>

        <span className="block w-full sm:hidden">
          <Button
            onClick={handleCheckout}
            size={isMobile ? 'lg' : 'md'}
            fullWidth
          >
            Buy gift card
          </Button>
        </span>

        {!hides.has('formats') && (
          <PickYourClassSection
            classIds={giftCard.class_ids}
            productType={
              giftCard.kind === 'MONEY' ? 'gift-card-money' : 'gift-card'
            }
          />
        )}

        {!hides.has('formats') && giftCard.kind === 'MONEY' && (
          <GiftCardPlansSection
            classPackIds={giftCard.class_pack_ids ?? []}
            subscriptionIds={giftCard.subscription_ids ?? []}
          />
        )}

        {!isFaqsLoading && faqs.length > 0 && (
          <>
            <Separator />
            <FaqSection faqs={faqs} className="max-sm:pt-0" />
          </>
        )}

        {termsDocs.length > 0 && (
          <>
            <Separator />
            <TermsLinkRow
              href={`/terms/gift-cards/${giftCard.slug}`}
              title="Gift card terms"
            />
          </>
        )}
      </Card>
    </PageContainer>
  );
};

export const GiftCardDetailsSkeleton = () => {
  const { hasBack } = useGoBack();

  return (
    <PageContainer>
      <Card contentClassName="p-5 sm:p-6 flex flex-col gap-6">
        {hasBack && (
          <>
            <div className="flex items-center justify-between">
              <Skeleton
                isRounded
                className="!h-11 w-[124px] sm:!h-10 sm:w-[108px]"
              />
              <Skeleton
                isRounded
                className="hidden !h-10 sm:block sm:w-[132px]"
              />
            </div>
            <Separator />
          </>
        )}

        <div className="flex flex-col gap-5 sm:flex-row sm:gap-16">
          <div className="order-2 flex flex-1 flex-col gap-5 sm:order-1 sm:gap-6">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="flex flex-1 flex-col gap-1">
                <Skeleton text="h2" width={208} />
                <Skeleton text="body-sm" width={288} />
                <Skeleton text="body-sm" width={224} />
              </div>
              {!hasBack && (
                <Skeleton
                  isRounded
                  className="hidden !h-10 shrink-0 sm:block sm:w-[132px]"
                />
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" className="w-2/3" />
            </div>
          </div>

          <div className="order-1 shrink-0 sm:order-2">
            <Skeleton
              isRounded
              width="100%"
              className="!h-auto !rounded-xl sm:!w-[280px]"
              style={{ aspectRatio: GIFT_CARD_ASPECT }}
            />
          </div>
        </div>

        <Skeleton
          isRounded
          height={44}
          width="100%"
          className="block sm:hidden"
        />
      </Card>
    </PageContainer>
  );
};
